import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Holidays from 'date-holidays';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { TrashIcon } from '@common_components/icons/trash.icon';
import SelectInput, { GroupedOption, Option as OldOption } from '@common_components/inputs/selects/selectInput.component';
import DatePickerInput from '@common_components/inputs/datePickerInput.component';
import { useTranslation } from 'react-i18next';
import { ClosedDay, PostClosedDayToEventManager } from 'interfaces/closedDays.interface';
import { CheckIcon, CommonGlobalService, CommonUtilsService, dayjs, FORMAT_DATE_API, MultiSelect, Option, RootState, UUID, ListPaginationResult } from '@umanforge/common';
import { EventManager } from 'interfaces/eventManager.interface';
import { HolidayLocation } from 'interfaces/holidayLocation.interface';
import { useDispatch, useSelector } from 'react-redux';
import { ClosedDaysService } from 'services/closedDays.service';
import { ToastService } from 'services/toast.service';
import Pagination from '@common_components/inputs/selects/pagination.component';

interface HolidayManagerProps {
  eventManager: EventManager | undefined;
  usePublicHolidays: boolean;
  setUsePublicHolidays: Dispatch<SetStateAction<boolean>>;
  eventManagerHolidays: HolidayLocation | null,
  addedClosedDays: ClosedDay[];
  setAddedClosedDays: Dispatch<SetStateAction<ClosedDay[]>>;
  removedClosedDays: UUID[];
  setRemovedClosedDays: Dispatch<SetStateAction<UUID[]>>;
  selectedCountry: string | null;
  setSelectedCountry: Dispatch<SetStateAction<string | null>>;
  selectedState: string | null;
  setSelectedState: Dispatch<SetStateAction<string | null>>;
  selectedRegion: string | null;
  setSelectedRegion: Dispatch<SetStateAction<string | null>>;
  selectedHolidays: { name: string, id: string }[];
  setSelectedHolidays: Dispatch<SetStateAction<{ name: string, id: string }[]>>;
}

const ADDED_CLOSED_DAY_ID = 'added_closed_day';

const HolidayManager: React.FC<HolidayManagerProps> = ( {
  eventManager,
  eventManagerHolidays,
  usePublicHolidays,
  setUsePublicHolidays,
  addedClosedDays,
  setAddedClosedDays,
  removedClosedDays,
  setRemovedClosedDays,
  selectedCountry,
  setSelectedCountry,
  selectedState,
  setSelectedState,
  selectedRegion,
  setSelectedRegion,
  selectedHolidays,
  setSelectedHolidays
} ) => {
  const toast = new ToastService( 'HolidayManagerComponent' );
  const { t } = useTranslation();
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const holidays = new Holidays();

  /*----------------------------- useState ------------------------------*/
  const [ eventManagerClosedDay, setEventManagerClosedDay ] = useState<{ total: number; datas: ClosedDay[]; }>( { total: 0, datas: [] } );

  const [ countries, setCountries ] = useState<GroupedOption[]>( [] );
  const [ states, setStates ] = useState<GroupedOption[]>( [] );
  const [ regions, setRegions ] = useState<GroupedOption[]>( [] );


  const [ page, setPage ] = useState<number>( 1 );
  const [ totalPages, setTotalPages ] = useState<number>( 1 );
  const [ step ] = useState<number>( 5 );

  const [ renderCountries, setRenderCountries ] = useState<number>( 0 );
  const [ renderStates, setRenderStates ] = useState<number>( 0 );
  const [ renderRegions, setRenderRegions ] = useState<number>( 0 );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  /*----------------------------- useEffect -----------------------------*/
  useEffect( () => {
    refreshClosedDays();
  }, [ eventManager, page ] );

  useEffect( () => {
    setUsePublicHolidays( eventManagerHolidays != null );
    const contriesUpdates = objectToGroupedOption( holidays.getCountries() );
    setCountries( contriesUpdates );
    if ( eventManagerHolidays ) {
      const { country, state, region } = eventManagerHolidays;
      setSelectedCountry( country );
      const statesUpdated = objectToGroupedOption( holidays.getStates( country ) );
      setStates( statesUpdated );
      setSelectedState( state );
      if ( state ) {
        const regionsUpdated = objectToGroupedOption( holidays.getRegions( country, state ) );
        setRegions( regionsUpdated );
        setSelectedRegion( region );
      }
    } else {
      setSelectedHolidays( [] );
    }

  }, [ eventManagerHolidays ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedState( null );
      if ( selectedCountry ) {
        setStates( objectToGroupedOption( holidays.getStates( selectedCountry ) ) );
      } else {
        setStates( [] );
      }
    }
  }, [ selectedCountry ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedRegion( null );
      if ( selectedCountry && selectedState ) {
        setRegions( objectToGroupedOption( holidays.getRegions( selectedCountry, selectedState ) ) );
      } else {
        setRegions( [] );
      }
    }
  }, [ selectedState ] );

  useEffect( () => {
    setRenderCountries( renderCountries + 1 );
  }, [ countries ] );
  useEffect( () => {
    setRenderStates( renderStates + 1 );
  }, [ states ] );
  useEffect( () => {
    setRenderRegions( renderRegions + 1 );
  }, [ regions ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      updateHolidays();
    }
  }, [ isFirstRender, selectedCountry, selectedState, selectedRegion ] );


  /*------------------------- Functions -------------------------*/
  const refreshClosedDays = async () => {
    let updatedClosedDays: { total: number, datas: ClosedDay[] } = { total: 0, datas: [] };

    if ( eventManager ) {
      updatedClosedDays = await ClosedDaysService.getClosedDaysByEventManager( eventManager.id, page, step, token, dispatch );
    }
    setEventManagerClosedDay( updatedClosedDays );
    setTotalPages( Math.ceil( updatedClosedDays.total / step ) );
    if ( isFirstRender ) {
      setTimeout( () => { setIsFirstRender( false ); }, 75 );
    }
  };

  const updateHolidays = () => {
    const holidayInstance = new Holidays();
    holidayInstance.init( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined, { types: [ 'public' ] } );

    setSelectedHolidays(
      holidayInstance.getHolidays( dayjs().year() )
        .filter( holiday => {
          if ( !( eventManagerHolidays?.country == selectedCountry ) ) {
            return true;
          }
          return eventManagerHolidays && eventManagerHolidays.excludedRules.indexOf( holiday.rule ) === -1;
        } )
        .sort( ( item1, item2 ) => { return dayjs( item1.start ).isAfter( dayjs( item2.start ) ) ? 1 : -1;  } )
        .map( holiday => { return {  name: holiday.name, id: holiday.rule }; } )
    );
  };

  const isValidClosedDayForEventManager = async ( dates: PostClosedDayToEventManager ) => {
    let isValid = true;
    if ( eventManager ) {
      const formatedDates = [ { startDate: dayjs( dates.startDate ), endDate: dayjs( dates.endDate ) } ];
      isValid = isValid && await ClosedDaysService.isValidClosedDaysForEventManager( eventManager.id, formatedDates, removedClosedDays, token, dispatch );
    }
    isValid = isValid && addedClosedDays.find( missingDay => {
      const startClosedDay = dayjs( missingDay.startDate );
      const endClosedDay = dayjs( missingDay.endDate );
      const startDate = dayjs( dates.startDate );
      const endDate = dayjs( dates.endDate );
      return !( startClosedDay.isAfter( endDate ) || endClosedDay.isBefore( startDate ) );
    } ) == null;
    return isValid;
  };

  const objectToGroupedOption = ( object: { [ key: string ]: string } ): GroupedOption[] => {
    const result: GroupedOption[] = [];
    if ( object ) {
      const options: OldOption[] = [];
      Object.keys( object ).map( key => {
        options.push( {
          label: object[ key ],
          id: key
        } );
      } );
      result.push( { options: CommonUtilsService.sortByKey( options, 'label', 'asc' ) } );
    }
    return result;
  };

  const getPromiseHolidays = ( country?: string, state?: string, region?: string ): Promise<ListPaginationResult<{ name: string, id: string }>> => {
    return new Promise( resolve => {
      const holidayInstance: Holidays = new Holidays();
      holidayInstance.init( country, state ?? undefined, region ?? undefined, { types: [ 'public' ] } );

      const allowedHolidays: Option<{ name: string, id: string }>[] = CommonGlobalService.toOptions(
        holidayInstance.getHolidays( dayjs().year() )
          .sort( ( item1, item2 ) => { return dayjs( item1.start ).isAfter( dayjs( item2.start ) ) ? 1 : -1;  } )
          .map( holiday => { return { name: holiday.name, id: holiday.rule }; } )
      );
      resolve( { total: allowedHolidays.length, list: allowedHolidays } );
    } );
  };

  /*------------------------- Events -------------------------*/
  const onChangeCountry = ( country: string ) => {
    if ( country !== selectedCountry ) {
      setSelectedCountry( country );
    } else {
      setSelectedCountry( null );
    }
  };

  const onChangeState = ( state: string ) => {
    if ( state !== selectedState ) {
      setSelectedState( state );
    } else {
      setSelectedState( null );
    }
  };

  const onChangeRegion = ( region: string ) => {
    if ( region !== selectedRegion ) {
      setSelectedRegion( region );
    } else {
      setSelectedRegion( null );
    }
  };

  const onDeleteClosedDay = ( closedDay: ClosedDay ) => {
    if ( closedDay.id != ADDED_CLOSED_DAY_ID ) {
      removedClosedDays.push( closedDay.id );
      setRemovedClosedDays( structuredClone( removedClosedDays ) );
    } else {
      const index = addedClosedDays.indexOf( closedDay );
      if ( index != -1 ) {
        addedClosedDays.splice( index, 1 );
        setAddedClosedDays( structuredClone( addedClosedDays ) );
      }
    }
  };

  const onUnDeleteClosedDay = async ( closedDay: ClosedDay ) => {
    if ( removedClosedDays.includes( closedDay.id ) ) {
      const isValideClosedDay = await isValidClosedDayForEventManager( { startDate: closedDay.startDate, endDate: closedDay.endDate } );
      if  ( isValideClosedDay ) {
        const index = removedClosedDays.indexOf( closedDay.id );
        if ( index != -1 ) {
          removedClosedDays.splice( index, 1 );
          setRemovedClosedDays( structuredClone( removedClosedDays ) );
        }
      } else {
        toast.showWarning( t( 'translation:a_closure_is_already_planned_for_this_period' ) );
      }
    }
  };

  const onAddClosedDay = async ( dates: PostClosedDayToEventManager ) => {
    const isValideClosedDay = await isValidClosedDayForEventManager( dates );
    if ( isValideClosedDay ) {
      if ( me ) {
        addedClosedDays.push( {
          id: ADDED_CLOSED_DAY_ID,
          eventManagerId: eventManager?.id ?? '',
          startDate: dates.startDate,
          endDate: dates.endDate,
          userAddedId: me?.id,
          userAddedFullname: me?.fullname,
        } );
        setAddedClosedDays( structuredClone( addedClosedDays ).sort( ( item1, item2 ) => { return dayjs( item1.startDate ).isAfter( dayjs( item2.startDate ) ) ? -1 : 1;  } ) );
      }
    } else {
      toast.showWarning( t( 'translation:a_closure_is_already_planned_for_this_period' ) );
    }
  };


  /*------------------------- Front --------------------------*/
  return (
    <div className='flex flex-col h-full w-full py-0 px-7 gap-6'>

      <div className='flex flex-col h-fit w-full gap-4'>
        <div className='flex gap-x-4 w-fit pt-2' onClick={() => {return setUsePublicHolidays( !usePublicHolidays );}}>
          <button className={`flex-center rounded-full h-10 w-10 shadow-md hover:bg-orange-base-veryLight hover:text-black trans-fast ${usePublicHolidays ? 'bg-orange-base-light' : 'bg-white'}`}>
            {usePublicHolidays ? <CheckIcon className='h-5 w-5 trans-fast appear-top text-white' /> : <PlusIcon size={5} className='trans-fast appear-top' />}
          </button>
          <p className='flex-center'>{t( 'common:add_public_holidays' )}</p>
        </div>
        <div
          className={`
            origin-top-left transition-all duration-500
            ${usePublicHolidays ? 'scale-100 h-100' : 'scale-0 invisible h-0'}
          `}
        >
          {
            usePublicHolidays ?
              <div className='flex flex-col gap-2'>
                <div className='flex flex-col md:flex-row gap-2 w-full'>
                  <div className='w-full md:w-1/3'>
                    <SelectInput
                      labelPosition='top'
                      isRequired
                      refreshTrigger={ renderCountries }
                      emptyGroupsMessage={ t( 'common:no_countries_available' ) }
                      placeholder={ t( 'common:select_a_country' ) }
                      label={ t( 'common:country' ) }
                      fetchOptions={ () => { return Promise.resolve( countries ); } }
                      onSelect={ onChangeCountry }
                      selected={ selectedCountry ?? undefined }
                    />
                  </div>
                  <div className='w-full md:w-1/3'>
                    <SelectInput
                      labelPosition='top'
                      refreshTrigger={ renderStates }
                      emptyGroupsMessage={ t( 'common:no_states_available' ) }
                      placeholder={ t( 'common:select_a_state' ) }
                      label={ t( 'common:state' ) }
                      fetchOptions={ () => { return Promise.resolve( states ); } }
                      onSelect={ onChangeState }
                      selected={ selectedState ?? '' }
                    />
                  </div>
                  <div className='w-full md:w-1/3'>
                    <SelectInput
                      labelPosition='top'
                      refreshTrigger={ renderRegions }
                      emptyGroupsMessage={ t( 'common:no_regions_available' ) }
                      placeholder={ t( 'common:select_a_region' ) }
                      label={ t( 'common:region' ) }
                      fetchOptions={ () => { return Promise.resolve( regions ); } }
                      onSelect={ onChangeRegion }
                      selected={ selectedRegion ?? '' }
                    />
                  </div>
                </div>
                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'common:public_holidays' ) }
                    getValues={ () => { return getPromiseHolidays( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined ); } }
                    selectedValues={ CommonGlobalService.toOptions( selectedHolidays ) }
                    onChange={ values => { setSelectedHolidays( values.map( value => { return value.object; } ) ); } }
                  />
                </div>
              </div>
              :
              null
          }
        </div>
      </div>

      <div className='flex flex-col h-full w-full gap-4'>
        <DatePickerInput
          label={ t( 'common:plan_a_closure' ) }
          labelPosition='top'
          textSize='w-full'
          closeAfterSelect
          clearAfrerClose
          onChange={ ( start, end ) => {
            onAddClosedDay( {
              startDate: dayjs( start ).format( FORMAT_DATE_API ),
              endDate: dayjs( end ).format( FORMAT_DATE_API )
            } );
          } }
        />

        <div
          className='rounded-lg border-[1px] border-gray-200 overflow-hidden'
          key={ eventManagerClosedDay.total + addedClosedDays.length + removedClosedDays.length }
        >
          <table className='w-full text-left gap-1'>
            <thead>
              <tr className='text-[16px] text-gray-500 border-b-2 border-gray-300'>
                <th className='px-4 py-2 text-gray-600 font-normal tracking-wider'> { t( 'common:date' ) } </th>
                <th className='px-4 py-2 text-gray-600 font-normal tracking-wider'> { t( 'common:by' ) } </th>
                <th></th>
              </tr>
            </thead>
            <>
              {
                addedClosedDays.length > 0 ?
                  <tbody className='border-b-2 border-gray-300'>
                    {
                      addedClosedDays.map( ( closedDay, index ) => {
                        const isDeleted = removedClosedDays.some( id => { return closedDay.id === id; } );
                        const startDate = dayjs( closedDay.startDate );
                        const endDate = dayjs( closedDay.endDate );
                        const displayRange = startDate.isSame( endDate, 'day' ) ? startDate.format( 'LL' ) : ( startDate.format( 'LL' ) + ' - ' + endDate.format( 'LL' ) );
                        return (
                          <tr
                            key={ closedDay.id + startDate + endDate }
                            className={
                              (
                                isDeleted ?
                                  ( index % 2 === 1 ? 'bg-red-200' : 'bg-red-100' )
                                  :
                                  ( index % 2 === 1 ? 'bg-sky-100' : 'bg-sky-50' )
                              )
                            }
                          >
                            <td className='px-4 py-3'>
                              { displayRange }
                              <span className="h-fit w-fit align-text-top bg-blue-200 text-blue-800 text-[10px] font-medium ml-2 px-1.5 py-0.5 rounded-md">
                                { t( 'common:new' ) }
                              </span>
                            </td>
                            <td className='px-4 py-3'> { closedDay.userAddedFullname } </td>
                            {
                              isDeleted ?
                                <td className='px-2 justify-items-center'>
                                  <PlusIcon
                                    size={ 6 }
                                    className='cursor-pointer transition ease-in-out delay-75 hover:text-green-500 hover:scale-105 active:scale-95'
                                    onClick={ () => { onAddClosedDay( closedDay ); } }
                                  />
                                </td>
                                :
                                <td className='px-2 justify-items-center'>
                                  <TrashIcon
                                    size={ 6 }
                                    className='cursor-pointer transition ease-in-out delay-75 hover:text-red-500 hover:scale-105 active:scale-95'
                                    onClick={ () => { onDeleteClosedDay( closedDay ); } }
                                  />
                                </td>
                            }
                          </tr>
                        );
                      } )
                    }
                  </tbody>
                  :
                  null
              }
              {
                eventManagerClosedDay.total > 0 ?
                  <tbody>
                    {
                      eventManagerClosedDay.datas.map( ( closedDay, index ) => {
                        const isDeleted = removedClosedDays.some( id => { return closedDay.id === id; } );
                        const startDate = dayjs( closedDay.startDate );
                        const endDate = dayjs( closedDay.endDate );
                        const displayRange = startDate.isSame( endDate, 'day' ) ? startDate.format( 'LL' ) : ( startDate.format( 'LL' ) + ' - ' + endDate.format( 'LL' ) );
                        return (
                          <tr
                            key={ closedDay.id + startDate + endDate }
                            className={
                              (
                                isDeleted ?
                                  ( index % 2 === 1 ? 'bg-red-200' : 'bg-red-100' )
                                  :
                                  ( index % 2 === 1 ? 'bg-gray-base-light' : 'bg-white' )
                              )
                            }
                          >
                            <td className='px-4 py-3'>
                              { displayRange }
                            </td>
                            <td className='px-4 py-3'> { closedDay.userAddedFullname } </td>
                            {
                              isDeleted ?
                                <td className='px-2 justify-items-center'>
                                  <PlusIcon
                                    size={ 6 }
                                    className='cursor-pointer transition ease-in-out delay-75 hover:text-green-500 hover:scale-105 active:scale-95'
                                    onClick={ () => { onUnDeleteClosedDay( closedDay ); } }
                                  />
                                </td>
                                :
                                <td className='px-2 justify-items-center'>
                                  <TrashIcon
                                    size={ 6 }
                                    className='cursor-pointer transition ease-in-out delay-75 hover:text-red-500 hover:scale-105 active:scale-95'
                                    onClick={ () => { onDeleteClosedDay( closedDay ); } }
                                  />
                                </td>
                            }
                          </tr>
                        );
                      } )
                    }
                  </tbody>
                  :
                  null
              }
            </>
          </table>
          {
            eventManagerClosedDay.total > 0 ?
              null
              :
              <div className='flex w-full py-2 bg-gray-50 items-center justify-center'>
                { t( 'translation:no_closing_days_are_recorded' ) }
              </div>
          }
        </div>
        {
          totalPages > 1 ?
            <Pagination
              currentPage={ page }
              totalPages={ totalPages }
              onPageChange={ setPage }
            />
            :
            null
        }
      </div>
    </div>
  );
};

export default HolidayManager;
